import { useLayoutEffect, useState, type RefObject } from 'react'

/** Gap between the trigger and the panel, either side. */
const PANEL_GAP = 6

/**
 * Where the fixed panel sits: right-aligned to the trigger, below it or flipped
 * above. `maxHeight` is set only when the panel fits on neither side: it is
 * then capped to the larger side's room and scrolls internally.
 */
export type Place = { right: number; top?: number; bottom?: number; maxHeight?: number }

/** What the shell pins to the viewport's edges, which a menu must not open underneath. */
export type Reserve = { top: number; bottom: number }

/**
 * The panel's place, from the trigger's viewport rect and the panel's natural
 * (unclamped) height.
 *
 * The usable band is the viewport minus `reserve`: the sticky top bar above,
 * the phone tab bar below. Both bars float over content at z-index 40 and the
 * panel stays in the content layer, so any part of it inside a reserve would be
 * painted over (N1: at 320×460–520 "Mark paid…" flipped up under the top bar).
 *
 * Below the trigger when the panel fits there; else above when it fits there;
 * else on the side with more room, capped to that room with `maxHeight` so
 * every item stays reachable by scrolling the panel. Each side's room starts at
 * the trigger edge clamped into the band, so a trigger scrolled partly under a
 * bar still never pushes the panel under it.
 */
export function placeMenu(
  trigger: Pick<DOMRect, 'top' | 'bottom' | 'right'>,
  panelHeight: number,
  viewport: { width: number; height: number },
  reserve: Reserve = { top: 0, bottom: 0 },
): Place {
  const right = Math.max(0, viewport.width - trigger.right)
  const bandTop = reserve.top
  const bandBottom = viewport.height - reserve.bottom
  const belowStart = Math.max(trigger.bottom + PANEL_GAP, bandTop)
  const aboveEnd = Math.min(trigger.top - PANEL_GAP, bandBottom)
  const below = bandBottom - belowStart
  const above = aboveEnd - bandTop
  const downward = { right, top: belowStart }
  const upward = { right, bottom: viewport.height - aboveEnd }
  if (panelHeight <= below) return downward
  if (panelHeight <= above) return upward
  return below >= above ? { ...downward, maxHeight: Math.max(0, below) } : { ...upward, maxHeight: Math.max(0, above) }
}

/**
 * The heights the shell pins to the top and bottom edges, read off two probes
 * inside the panel: one `var(--topbar-h)` tall (the sticky top bar, at every
 * width), one `var(--tabbar-h)` on a phone and 0 otherwise. Measured boxes
 * rather than parsed custom properties: a token may be a `calc()`, and the
 * layout engine is what resolves it.
 */
const probe = (panel: HTMLElement | null, edge: 'top' | 'bottom'): number =>
  panel?.querySelector<HTMLElement>(`.rec-menu__reserve--${edge}`)?.offsetHeight ?? 0

/**
 * The panel's natural border-box height. Once a `maxHeight` caps it,
 * `offsetHeight` reads the cap, which would then "fit" and drop the cap on the
 * next pass, oscillating; `scrollHeight` still carries the full content.
 */
const naturalHeight = (panel: HTMLElement | null): number =>
  panel ? panel.scrollHeight + panel.offsetHeight - panel.clientHeight : 0

function measure(trigger: HTMLElement, panel: HTMLElement | null): Place {
  const root = document.documentElement
  return placeMenu(
    trigger.getBoundingClientRect(),
    naturalHeight(panel),
    { width: root.clientWidth, height: root.clientHeight },
    { top: probe(panel, 'top'), bottom: probe(panel, 'bottom') },
  )
}

/**
 * Fixed-position placement for a panel anchored to a trigger, for a panel
 * portalled out of the clipping containers its trigger lives in. Recomputed on
 * open, on any scroll (capture: a scroller's own scroll counts) and on resize,
 * then once more when the panel has a measured height (the first pass had
 * none). `placed` flips once per open (`place` resets to null on close), so a
 * caller can key one-shot work such as moving focus in to it; a re-place on
 * scroll never flips it again.
 *
 * `remeasure` is any value whose change alters the panel's box (the phone
 * skin), so the placement is recomputed when it changes.
 */
export function useAnchoredPlacement(
  triggerRef: RefObject<HTMLElement | null>,
  panelRef: RefObject<HTMLElement | null>,
  open: boolean,
  remeasure?: unknown,
): { place: Place | null; placed: boolean } {
  const [place, setPlace] = useState<Place | null>(null)

  useLayoutEffect(() => {
    if (!open) {
      setPlace(null)
      return
    }
    function update() {
      const trigger = triggerRef.current
      if (!trigger) return
      setPlace(measure(trigger, panelRef.current))
    }
    update()
    window.addEventListener('scroll', update, true)
    window.addEventListener('resize', update)
    return () => {
      window.removeEventListener('scroll', update, true)
      window.removeEventListener('resize', update)
    }
  }, [open, remeasure, triggerRef, panelRef])

  useLayoutEffect(() => {
    if (!open || !place || !triggerRef.current || !panelRef.current) return
    const next = measure(triggerRef.current, panelRef.current)
    if (
      next.top !== place.top ||
      next.bottom !== place.bottom ||
      next.right !== place.right ||
      next.maxHeight !== place.maxHeight
    )
      setPlace(next)
  }, [open, place, remeasure, triggerRef, panelRef])

  return { place, placed: place !== null }
}
